import {type SpacePublicInfo} from "@src/features/auth/auth-models";
import {selectSpacesPublicInfo} from "@src/features/auth/redux/auth-slice";
import {useAppSelector} from "@src/integrations/redux/redux-store";
import {Avatar, AvatarFallback, AvatarImage} from "@src/ui/views/avatar";
import {Button} from "@src/ui/views/button";
import {Card, CardContent, CardDescription, CardHeader, CardTitle,} from "@src/ui/views/card";
import {Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList} from "@src/ui/views/command";
import {ScrollArea} from "@src/ui/views/scroll-area";
import {Tooltip, TooltipContent, TooltipTrigger} from "@src/ui/views/tooltip";
import {Building2, ChevronRight} from "lucide-react";
import * as React from "react";
import {NavLink, useNavigate} from "react-router-dom";


type SpaceItemProps = {
	spacePublicInfo: SpacePublicInfo
}


export function DashboardsPickerScreen() {
	const spacesPublicInfo = useAppSelector(selectSpacesPublicInfo)!;
	
	const navigate = useNavigate();
	
	React.useEffect(() => {
		if (spacesPublicInfo.length === 1) {
			const defaultSpace = spacesPublicInfo[0];
			navigate(`/dashboard/${defaultSpace.spaceId}`);
		}
	}, [spacesPublicInfo, navigate]);
	
	if (spacesPublicInfo.length == 0) {
		return (
			<p>
				Seems like you don't have access to any Space... If that's a mistake, please contact support.
			</p>
		);
	}
	
	if (spacesPublicInfo.length === 1) {
		const defaultSpace = spacesPublicInfo[0];
		return (
			<p>
				{`Redirecting to ${defaultSpace.name}'s dashboard`}
			</p>
		);
	}
	
	return (
		<div className="w-full h-full flex justify-center">
			<Card className="w-4/5 h-full flex flex-col shadow-xl">
				<CardHeader className="flex-none">
					<CardTitle>Choose a Space</CardTitle>
					<CardDescription>You can view the dashboard for one of these Spaces:</CardDescription>
				</CardHeader>
				<CardContent className="grow overflow-y-auto">
					<Command className="rounded-xl border shadow-md">
						<CommandInput
							className="my-4 bg-secondary/70"
							placeholder="Search Spaces..."/>
						<ScrollArea className="px-4">
							<CommandList className="max-h-full">
								<CommandEmpty>No Spaces found.</CommandEmpty>
								<CommandGroup className="w-full">
									{
										spacesPublicInfo.map((spacePublicInfo) => (
											<SpaceItem
												key={spacePublicInfo.spaceId}
												spacePublicInfo={spacePublicInfo}
											/>
										))
									}
								</CommandGroup>
							</CommandList>
						</ScrollArea>
					</Command>
				</CardContent>
			</Card>
		</div>
	);
}


function SpaceItem(props: SpaceItemProps) {
	return (
		<CommandItem className="w-full h-24 rounded-3xl m-4 hover:shadow-md">
			<Tooltip>
				<TooltipTrigger asChild>
					<Button variant="link" asChild>
						<NavLink to={`/dashboard/${props.spacePublicInfo.spaceId}`} className="w-full h-full">
							<div className="w-full h-full flex items-center justify-between">
								<div className="flex items-center flex-none ml-3 w-fit h-fit space-x-8">
									<Avatar className="size-20 ">
										{
											props.spacePublicInfo.pictureUrl && (
												<AvatarImage
													src={props.spacePublicInfo.pictureUrl}
													alt={`${props.spacePublicInfo.name}'s dashboard`}
												/>
											)
										}
										<AvatarFallback>
											<Building2 className="size-16"/>
										</AvatarFallback>
									</Avatar>
									<span className="text-lg font-medium leading-none">
									{props.spacePublicInfo.name}
								</span>
								</div>
								<ChevronRight className="size-10 rounded-full overflow-clip hover:shadow-inner"/>
							</div>
						</NavLink>
					</Button>
				</TooltipTrigger>
				<TooltipContent>
					<p className="text-sm font-medium leading-none">
						{`Go to ${props.spacePublicInfo.name}'s dashboard`}
					</p>
				</TooltipContent>
			</Tooltip>
		</CommandItem>
	);
}
